import "@/styles/szadmin.css";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Eye, EyeOff } from "lucide-react";

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error("Invalid reset link");
      navigate('/szadmin');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-password`,
        {
          token: token,
          new_password: formData.newPassword
        }
      );

      if (response.data.success) {
        toast.success("Password reset successfully! Please login with your new password.");
        navigate('/szadmin');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || "Failed to reset password. Link may be expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="sz-root relative flex h-dvh w-full items-center justify-center overflow-hidden p-6"
      style={{ background: "#0C1120" }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(640px 480px at 78% 16%, rgba(120,40,124,.5), transparent 60%), radial-gradient(560px 460px at 18% 92%, rgba(40,192,208,.28), transparent 60%)",
        }}
      />

      <div
        className="sz-fade relative w-full max-w-[420px] rounded-[22px] bg-white px-[34px] py-9"
        style={{ boxShadow: "0 40px 90px -30px rgba(0,0,0,.6)" }}
      >
        <div
          className="mb-5 flex h-[54px] w-[54px] items-center justify-center rounded-[15px]"
          style={{ background: "linear-gradient(135deg,#572B7C,#28C0D0)" }}
        >
          <Shield className="h-[28px] w-[28px] text-white" strokeWidth={2} />
        </div>
        <h1 className="sz-display mb-2 text-[23px] font-bold tracking-[-.4px]">
          Set a new password
        </h1>
        <p
          className="mb-6 text-[13.5px] leading-[1.6]"
          style={{ color: "#8A90A2" }}
        >
          Choose a strong password you haven't used before. Minimum 6
          characters.
        </p>

        <form onSubmit={handleSubmit}>
          {/* New password */}
          <label
            className="mb-[7px] block text-[12.5px] font-semibold"
            style={{ color: "#2B3142" }}
            htmlFor="newPassword"
          >
            New password
          </label>
          <div className="relative mb-4">
            <input
              id="newPassword"
              type={showPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={(e) =>
                setFormData({ ...formData, newPassword: e.target.value })
              }
              placeholder="••••••••"
              className="sz-input text-sm"
              style={{ padding: "12px 44px 12px 14px", borderRadius: "12px" }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-lg bg-transparent"
              style={{ color: "#A8AEBC" }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-[17px] w-[17px]" />
              ) : (
                <Eye className="h-[17px] w-[17px]" />
              )}
            </button>
          </div>

          {/* Confirm password */}
          <label
            className="mb-[7px] block text-[12.5px] font-semibold"
            style={{ color: "#2B3142" }}
            htmlFor="confirmPassword"
          >
            Confirm password
          </label>
          <div className="relative mb-5">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
              placeholder="••••••••"
              className="sz-input text-sm"
              style={{ padding: "12px 44px 12px 14px", borderRadius: "12px" }}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-2 top-1/2 flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-lg bg-transparent"
              style={{ color: "#A8AEBC" }}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              {showConfirmPassword ? (
                <EyeOff className="h-[17px] w-[17px]" />
              ) : (
                <Eye className="h-[17px] w-[17px]" />
              )}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="sz-btn sz-btn-primary w-full"
            style={{ padding: "14px", borderRadius: "12px", fontSize: "14px" }}
          >
            {loading ? "Resetting Password..." : "Reset Password"}
          </button>

          <div className="mt-5 text-center">
            <Link
              to="/szadmin"
              className="text-[12.5px] font-semibold no-underline"
              style={{ color: "#572B7C" }}
            >
              ← Back to Admin Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminResetPassword;
